import { HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteRecordRaw,
  Router,
  createMemoryHistory,
  createRouter,
  createWebHistory,
} from 'vue-router';

import { DEFAULT_LANGUAGE, setLocaleBeforeRouteChange } from '@/i18n-setup';
import { getRouteName } from '@/router/localizedRouting';

import { createAuth0Instance, refreshAuthProfileAndSyncStore } from '@/helpers/Auth/authClient';
import { getCurrentPlatform, isServer } from '@/helpers/Platform/Platform';
import { getLoginCallbackUrl, isHowPage } from '@/helpers/Route/routeHelper';
import { AppState } from '@/store/types';
import { Pages } from '@/types';
import { Auth0Plugin } from '@auth0/auth0-vue';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';
import { Store } from 'vuex';
import getCreatePropertyFromInsertionOnDemandRoute from './routes/createPropertyFromInsertionOnDemand';
import getDetailsPageRoute from './routes/detailsPage';
import getListingRedirectRoute from './routes/listingRedirect';
import getLoginPageRoute from './routes/loginPage';
import getMyPropertiesLandingPageRoute from './routes/myPropertiesLandingPage';
import getPropertyEditPageRoute from './routes/propertyEditPage';
import getPropertyOverviewRoute from './routes/propertyOverview';
import getValuationFormRoute from './routes/valuationForm';

export function getAllRoutes(): RouteRecordRaw[] {
  return [
    ...getCreatePropertyFromInsertionOnDemandRoute(),
    ...getDetailsPageRoute(),
    ...getPropertyEditPageRoute(),
    ...getPropertyOverviewRoute(),
    ...getMyPropertiesLandingPageRoute(),
    ...getLoginPageRoute(),
    ...getValuationFormRoute(),
    ...getListingRedirectRoute(),
    {
      path: getLoginCallbackUrl(false),
      name: Pages.LoginCallback,
      component: () => import('@/pages/Login/LoginCallback.vue'),
      meta: {
        getLanguageSwitchLink(route: RouteRecordRaw, locale: HgSupportedLanguage) {
          return {
            name: route.name,
            params: {
              locale,
            },
          };
        },
      },
    },
    {
      path: '/:pageNotFound*',
      name: 'pageNotFound',
      component: () => import('@smg-real-estate/domus/HgPageNotFound.vue'),
      props: () => ({
        pageLayoutCssClass: 'layout-container',
        theme: getCurrentPlatform(),
      }),
      meta: {
        getLanguageSwitchLink(route: RouteRecordRaw) {
          return {
            path: route.path,
          };
        },
      },
    },
  ];
}

const getHistoryBase = (): string => import.meta.env.BASE_URL || '/';

type ValuationGuardAuth = Pick<Auth0Plugin, 'isAuthenticated' | 'loginWithRedirect'>;

export function createAppRouter(): Router {
  return createRouter({
    history: isServer()
      ? createMemoryHistory(getHistoryBase())
      : createWebHistory(getHistoryBase()),
    routes: getAllRoutes(),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition;
      }
      if (to.meta?.scrollToTop) {
        return { left: 0, top: 0 };
      }
      return false;
    },
  });
}

export const router = createAppRouter();

function getSanitizedTargetPath(to: RouteLocationNormalized, router: Router): string {
  const { login_required: _omitted, ...queryWithoutLoginRequired } = to.query;

  return router.resolve({
    path: to.path,
    query: queryWithoutLoginRequired,
    hash: to.hash,
  }).fullPath;
}

async function handleValuationFormAuth(
  to: RouteLocationNormalized,
  locale: HgSupportedLanguage,
  auth: ValuationGuardAuth,
  router: Router,
  next: NavigationGuardNext,
): Promise<boolean> {
  const isValuationFormRoute = getRouteName(to.name) === Pages.PropertyValuationForm;
  const isLoginRequired = to.query.login_required === 'true';

  if (!isValuationFormRoute || !isLoginRequired) {
    return false;
  }

  const { login_required: _omitted, ...queryWithoutLoginRequired } = to.query;

  if (!auth.isAuthenticated.value) {
    await auth.loginWithRedirect({
      authorizationParams: {
        ui_locales: locale,
      },
      appState: {
        target: getSanitizedTargetPath(to, router),
      },
    });
    next(false);
    return true;
  }

  next({
    path: to.path,
    query: queryWithoutLoginRequired,
    hash: to.hash,
    replace: true,
  });
  return true;
}

async function syncClientAuthSession(store: Store<AppState>): Promise<Auth0Plugin> {
  const auth0 = createAuth0Instance() as Auth0Plugin;

  try {
    await auth0.checkSession();
  } catch (error) {
    // ignore
  }

  await refreshAuthProfileAndSyncStore(store, auth0);

  return auth0;
}

export function initGuards(router: Router, store: Store<AppState>) {
  router.beforeEach(async (to, from, next) => {
    if (!isHowPage(to.name as `${Pages}-${HgSupportedLanguage}`) && from.path !== '/') {
      if (!isServer()) {
        window.location.href = `${import.meta.env.VUE_APP_BASE_URL}${to.fullPath}`;
      }
      return next(false);
    }

    const locale =
      ((to?.params?.locale ?? to?.meta?.locale) as HgSupportedLanguage) || DEFAULT_LANGUAGE;
    await setLocaleBeforeRouteChange(locale);

    if (isServer()) {
      next();
      return;
    }

    const auth0 = await syncClientAuthSession(store);
    const { isAuthenticated, loginWithRedirect } = auth0;
    const auth = { isAuthenticated, loginWithRedirect };

    if (await handleValuationFormAuth(to, locale, auth, router, next)) {
      return;
    }

    if (to.meta?.requiresAuth && !isAuthenticated.value) {
      await loginWithRedirect({
        authorizationParams: {
          ui_locales: locale,
        },
        appState: {
          target: to.fullPath,
        },
      });
      next(false);
    }

    next();
  });

  setupRumViewTracking(router);
}
